<script setup lang="ts">
import type { PaymentModelLoadErrorCode } from '~/ui-blocks/Formalization/blocks/paymentModel/paymentModel.types'
import { SolButton, SolEmptyState } from '@solfacil/girassol'
import { getErrorMessage } from '~/utils/errors'

interface LoadErrorContent {
  titleKey: string
  subtitleKey: string
  actionKey: string
  variant: 'error404' | 'no-content'
  fullWidth: boolean
}

const { error } = defineProps<{
  error: unknown
}>()

const emit = defineEmits<{
  retry: []
}>()

const { t } = useI18n()

const LOAD_ERROR_CONTENT: Record<PaymentModelLoadErrorCode, LoadErrorContent> = {
  404: {
    titleKey: 'receiptModel.error.title',
    subtitleKey: 'receiptModel.error.subtitle',
    actionKey: 'receipt-model.load_error.not_found.action',
    variant: 'error404',
    fullWidth: false,
  },
  422: {
    titleKey: 'receipt-model.load_error.unprocessable.title',
    subtitleKey: 'receipt-model.load_error.unprocessable.subtitle',
    actionKey: 'receipt-model.load_error.unprocessable.action',
    variant: 'no-content',
    fullWidth: true,
  },
}

function isLoadErrorCode(code: string): code is PaymentModelLoadErrorCode {
  return Object.prototype.hasOwnProperty.call(LOAD_ERROR_CONTENT, code)
}

const content = computed(() => {
  const code = getErrorMessage(error)
  return isLoadErrorCode(code) ? LOAD_ERROR_CONTENT[code] : null
})
</script>

<template>
  <div v-if="content" class="flex justify-center">
    <SolEmptyState
      id="empty-list"
      :title="t(content.titleKey)"
      :subtitle="t(content.subtitleKey)"
      :variant="content.variant"
      :class="{ 'w-full': content.fullWidth }"
    >
      <template #action>
        <div class="flex gap-2 inline-flex show-md">
          <SolButton
            id="btn-new-simulation"
            size="medium"
            @click="emit('retry')"
          >
            <template #icon-left>
              <IconPlus />
            </template>
            {{ t(content.actionKey) }}
          </SolButton>
        </div>
      </template>
    </SolEmptyState>
  </div>
</template>
